import { DUPE_API_URL } from '@dupe/common/constants'
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query'
import useAuthUser from '~/src/hooks/useAuthUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import fetcher from '~/src/lib/fetcher'
import type { RecentsResponse, UnifiedRecentItem } from './useFetchRecents'

interface Props {
  limit?: number
  enabled?: boolean
  orderBy?: 'asc' | 'desc'
  includeTotal?: boolean
  includeChats?: boolean
  types?: ('dupe' | 'chat')[]
}

const useFetchRecentsInfinite = (props?: Props) => {
  const {
    limit = 10,
    enabled = true,
    orderBy = 'desc',
    includeTotal = true,
    includeChats = true,
    types,
  } = props || {}
  const { tempUserId } = useGetTempUserId()
  const { authUser, loading: loadingAuthUser } = useAuthUser()
  const fetchUrl = `${DUPE_API_URL}/api/recents`

  const id = authUser?.uid || tempUserId
  const loading = loadingAuthUser

  const query = useInfiniteQuery<
    RecentsResponse,
    Error,
    InfiniteData<RecentsResponse>,
    (string | number | boolean | string[] | undefined)[],
    number
  >({
    queryKey: [
      'recents-infinite',
      id,
      limit,
      orderBy,
      includeTotal,
      includeChats,
      types,
      authUser?.uid,
      tempUserId,
    ],
    queryFn: ({ pageParam = 0 }) =>
      fetcher<RecentsResponse>(fetchUrl, {
        method: 'GET',
        dataOrParams: {
          limit,
          offset: pageParam,
          orderBy,
          includeTotal,
          includeChats,
          types,
        },
        tempUserId,
        authUser,
      }),
    getNextPageParam: (lastPage, allPages) => {
      // Check if we have more data to load
      const currentTotalItems = allPages.reduce(
        (acc, page) => acc + (page.recents?.length ?? 0),
        0
      )

      // If we have a total and we've loaded all items, return undefined
      if (lastPage.total && currentTotalItems >= lastPage.total) {
        return undefined
      }

      // If the last page had fewer items than the limit, we're done
      if (lastPage.recents?.length < limit) {
        return undefined
      }

      // Otherwise, return the next offset
      return currentTotalItems
    },
    initialPageParam: 0,
    enabled: !!id && enabled && !loading,
    // Prevent multiple requests on window focus by increasing stale time
    // This means data won't be considered stale for 5 minutes
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep cache for 10 minutes (must be >= staleTime)
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    // Only refetch if data is actually stale
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Prevent refetching if data is still fresh
    refetchInterval: false,
  })

  // Extract the flattened recents array and total count
  const recents = query.data?.pages?.flatMap((page) => page.recents) ?? []
  const total = query.data?.pages?.[0]?.total ?? 0

  return {
    ...query,
    data: recents as UnifiedRecentItem[],
    total,
  }
}

export default useFetchRecentsInfinite
